import type { CampaignRecord } from '@navio/nsdk';
import { revalidateLogic } from '@tanstack/react-form';
import { invalidateAfterCampaignMutation, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiSheet, useAppForm } from 'noddi-ui-web';
import { useEffect } from 'react';
import { z } from 'zod';
import { expiryFromDay } from '../../expiryFromDay';
import { usePromotionBrandScope } from '../../usePromotionBrandScope';
import { CampaignVerificationCodeField } from '../CampaignVerificationCodeField';
import { CAMPAIGN_AUDIENCE, CAMPAIGN_AUDIENCE_OPTIONS } from '../create/createCampaignSchema';

const FORM_ID = 'edit-campaign-form';

const validationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().min(1, 'Description is required'),
  audienceKind: z.enum([CAMPAIGN_AUDIENCE.anyone, CAMPAIGN_AUDIENCE.new_customers]),
  expiresAt: z.date().nullish(),
  expiryDaysAfterReceiving: z.number().int().positive().nullable(),
  authCode: z.string()
});

type FormValues = z.input<typeof validationSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
  campaign: CampaignRecord;
};

const getDefaultValues = (campaign: CampaignRecord): FormValues => ({
  name: campaign.name,
  description: campaign.description,
  audienceKind: campaign.isForNewUserOnly ? CAMPAIGN_AUDIENCE.new_customers : CAMPAIGN_AUDIENCE.anyone,
  expiresAt: campaign.expiresAt ? new Date(campaign.expiresAt) : null,
  expiryDaysAfterReceiving: campaign.couponTemplates[0]?.expiryDaysAfterReceiving ?? null,
  authCode: campaign.authCode ?? ''
});

export const EditCampaignSheet = ({ open, onClose, campaign }: Props) => {
  const { serviceOrganizationId } = usePromotionBrandScope();
  const { mutateAsync: patchCampaign } = noddiAsync.usePatch({
    type: URLKeys.patchCampaign,
    queryConfig: {
      onSuccess: () => {
        void invalidateAfterCampaignMutation({ campaignId: campaign.id });
      }
    }
  });

  const form = useAppForm({
    defaultValues: getDefaultValues(campaign),
    validationLogic: revalidateLogic(),
    validators: { onSubmit: validationSchema, onDynamic: validationSchema },
    onSubmit: async ({ value }) => {
      await patchCampaign({
        campaignId: campaign.id,
        serviceOrganizationId,
        name: value.name,
        description: value.description,
        isForNewUserOnly: value.audienceKind === CAMPAIGN_AUDIENCE.new_customers,
        expiresAt: value.expiresAt ? expiryFromDay(value.expiresAt) : null,
        expiryDaysAfterReceiving: value.expiryDaysAfterReceiving,
        authCode: value.authCode.trim()
      });
      onClose();
    }
  });

  useEffect(() => {
    if (open) {
      form.reset(getDefaultValues(campaign));
    }
  }, [open, campaign, form]);

  return (
    <form.AppForm>
      <NoddiSheet
        open={open}
        onOpenChange={(next) => !next && onClose()}
        title='Edit campaign'
        footer={<form.SubscribeButton formId={FORM_ID} label='Save' className='w-full' />}
      >
        <form
          id={FORM_ID}
          className='flex flex-col gap-5'
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.AppField name='name'>
            {(field) => <field.Input label='Name' required tooltip='Shown to customers on the claim page.' />}
          </form.AppField>
          <form.AppField name='description'>
            {(field) => (
              <field.TextArea
                label='Description'
                rows={4}
                required
                tooltip='Extra details customers see when they claim the offer.'
              />
            )}
          </form.AppField>
          <form.AppField name='audienceKind'>
            {(field) => (
              <field.Select
                label='Audience'
                tooltip='Who is allowed to claim this campaign.'
                options={CAMPAIGN_AUDIENCE_OPTIONS}
              />
            )}
          </form.AppField>
          <form.AppField name='expiryDaysAfterReceiving'>
            {(field) => (
              <field.Input
                label='Expires after claim (optional)'
                tooltip='How many days the customer has to use the coupon after they claim it.'
                type='number'
                endAdornment={<p className='text-sm text-muted-foreground'>days</p>}
              />
            )}
          </form.AppField>
          <form.AppField name='expiresAt'>
            {(field) => (
              <field.DatePicker
                label='Claim deadline (optional)'
                tooltip='Last day customers can claim this campaign. Leave empty for no deadline.'
                showClearButton
              />
            )}
          </form.AppField>
          <form.AppField name='authCode'>
            {(field) => (
              <CampaignVerificationCodeField value={field.state.value} onChange={(next) => field.handleChange(next)} />
            )}
          </form.AppField>
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
